import { Injectable, Optional } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { tap, switchMap, concatMap } from 'rxjs/operators';

import { BindingList, FrameContext, ViewModel } from '@farris/devkit';
import { BaseDataService } from './base-data.service';
import { FormMessageService } from '../form-message.service';
import { FormLoadingService } from '../form-loading/form-loading.service';
import { FormErrorService } from '../error/form-error.service';
import { FormNotifyService } from '../form-notify.service';
import { LanguageService } from '../languag.service';
import { TreeRepositoryFactory } from './tree-table/repository/tree-repository-factory';
import { TreeUtilFactory } from './tree-table/util/tree-util-factory';



/**
 * æ æ°æ®æå¡
 */
@Injectable()
class SubTreeDataService extends BaseDataService {

  /**
   * åçº§ç ä¿¡æ¯
   */
  private get hierarchyInfoKey(): string {
    return this.virtualRootFrameContext.getParam('hierarchyInfoKey');
  }
  private get hierarchyInfoField(): string {
    return this.hierarchyInfoKey.split('/').filter(p => p).pop();
  }
  private get virtualRootFrameContext() {
    return this.frameContext.getVirtualRootFrameContext();
  }

  /**
   * æé å½æ°
   */
  constructor(
    frameContext: FrameContext,
    private viewModel: ViewModel,
    private messageService: FormMessageService,
    private loadingService: FormLoadingService,
    private errorService: FormErrorService,
    private formNotifyService: FormNotifyService,
    @Optional() private languageService: LanguageService
  ) {
    super(frameContext);
    if (!languageService) {
      this.languageService = LanguageService.getInstance();
    }
  }

  private get messagePipe() {
    if (this.viewModel && this.viewModel.frameContext) {
      const appContext = this.viewModel.frameContext.getFormAppContext() || null;
      if (appContext) {
        return appContext.messagePipe || null;
      }
    }
    return null;
  }

  /**
   * æ°å¢å­è¡¨åçº§
   */
  public addSubSibling(): Observable<any> {
    if (this.messagePipe) {
      this.messagePipe.next({ messageType: 'addSubSibling' });
    }
    let params: { nodeCodes: string[], ids: string[] } = this.getParams();
    // è·ååçº§æ¹å¼
    const hierarchyType: string = this.getHierarchyType();
    // æ§è¡åæ°
    const loadingTimerId = this.loadingService.showLoadingWithDelay(500);
    const repository = TreeRepositoryFactory.getInstance(hierarchyType);
    if (!repository) {
      // éè¯¯çåçº§ç 
      throw new Error(this.languageService['errorHierarchyKey']);
    }
    const result$ = repository.addSubSibling(this.repository, params.nodeCodes, params.ids);
    return result$.pipe(
      tap(
        () => {
          this.loadingService.hideDelayLoading(loadingTimerId);
        },
        error => {
          this.errorService.exception(this.languageService.addSubSiblingFailed, error);
        }
      )
    );
  }

  /**
   * æ°å¢ä¸çº§
   */
  public addSubChild(): Observable<any> {
    if (this.messagePipe) {
      this.messagePipe.next({ messageType: 'addSubChild' });
    }
    let params: { nodeCodes: string[], ids: string[] } = this.getParams();
    // è·ååçº§æ¹å¼
    const hierarchyType = this.getHierarchyType();

    const currentList = this.frameContext && this.frameContext.bindingData && this.frameContext.bindingData.getList();
    if (!currentList['currentId']) {
      // è¯·éæ©ç¶èç¹
      this.formNotifyService.warning(this.languageService['plsSelectParentNode'], { hideTitle: true });
      return EMPTY;
    }
    // æ§è¡åæ°
    const loadingTimerId = this.loadingService.showLoadingWithDelay(500);

    const repository = TreeRepositoryFactory.getInstance(hierarchyType);
    if (!repository) {
      throw new Error(this.languageService['errorHierarchyKey']);
    }
    const addSubChild$ = repository.addSubChild(this.repository, params.nodeCodes, params.ids);
    const result$ = addSubChild$.pipe(
      tap(
        () => {
          this.loadingService.hideDelayLoading(loadingTimerId);
        },
        error => {
          this.loadingService.hideDelayLoading(loadingTimerId);
          this.errorService.exception(this.languageService.addSubChildFailed, error);
        }
      )
    );
    return result$;
  }
  /**
   * å é¤å­è¡¨æ èç¹
   * @param id id
   */
  public remove(id: string, successMsg?: string): Observable<any> {
    // åæ°æ£æ¥
    if (!id) {
      this.formNotifyService.warning(this.languageService['plsSelectDeleteData'], { hideTitle: true });
      return EMPTY;
    }
    // è·ååçº§æ¹å¼
    const hierarchyType = this.getHierarchyType();
    // æå­èç¹æ¶ä¸åè®¸å é¤
    const treeNodesData = this.frameContext.bindingData.getList().toJSON();
    const bindingList = this.frameContext.bindingData.getList();
    const treeNodeUtil = TreeUtilFactory.getInstance(hierarchyType);
    if (treeNodeUtil === null) {
      return EMPTY;
    }
    if (treeNodeUtil.hasChildNodes(treeNodesData, this.hierarchyInfoField, id) === true) {
      this.messageService.warning(this.languageService['deleteChildFirst']);
      return EMPTY;
    }
    // ç¡®è®¤å é¤
    const action$ = this.messageService.question(this.languageService.confirmDeletion);
    return action$.pipe(
      concatMap(result => {
        if (!result) {
          return EMPTY;
        }
        // è·åå é¤åè¦è®¾ç½®çèç¹id
        const nextNodeId = treeNodeUtil.getNextNodeId(treeNodesData, this.hierarchyInfoField, id);

        // æ§è¡å é¤
        const loadingTimerId = this.loadingService.show();
        const path = this.getPath();
        const remove$ = this.frameContext.repository.removeByPath(path, id);
        return remove$.pipe(
          tap(() => {
            // è®¾ç½®éä¸­èç¹
            treeNodeUtil.selectNodeByBindingList(bindingList, this.hierarchyInfoField, nextNodeId);
            this.loadingService.hideDelayLoading(loadingTimerId);
            if (successMsg && successMsg.trim()) {
              let showMessage: boolean = true;
              if (successMsg.startsWith('{') && successMsg.endsWith('}')) {
                try {
                  const options = JSON.parse(successMsg);
                  if (options.showMessage === false) {
                    showMessage = false;
                  }
                } catch { }
              }
              if (showMessage !== false) {
                this.formNotifyService.success(successMsg, { hideTitle: true });
              }
            } else {
              this.formNotifyService.success(this.languageService.deleteSuccess, { hideTitle: true });
            }
            // this.formNotifyService.success(this.languageService.deleteSuccess, { hideTitle: true });
          },
            error => {
              this.loadingService.hideDelayLoading(loadingTimerId);
              this.errorService.exception(this.languageService.deleteFailed, error);
            }
          )
        );
      })
    );
  }

  private getHierarchyType() {
    const propInfo = this.repository.entityTypeInfo.getPropInfoByPath(this.hierarchyInfoKey.split('/'));
    let hierarchyType = propInfo.metadataInfo['hierarchyType'] || null;
    if (hierarchyType == null || hierarchyType.length < 1) {
      // 'åçº§ç éç½®ä¿¡æ¯éè¯¯'
      throw new Error(this.languageService['incorrectHierarchyConfig']);
    }
    return hierarchyType;
  }

  /**
   * è·ååæ°
   * /parentId/childCodes/childId/grandsonCodes
   * [childCodes, grandsonCodes]
   * [parntId, childId]
   */
  private getParams(): { nodeCodes: string[], ids: string[] } {
    let nodeCodes = this.viewModel.bindingPath.substr(1).split('/');
    let ids = [];
    let nodeCodeArray = [];
    const rid = this.viewModel.bindingData.list.currentId; // rootè¡¨æ°æ®id
    ids.push(rid);
    let subData: any = this.viewModel.bindingData;
    if (nodeCodes.length > 0) {
      nodeCodes.map(nodeCode => {
        subData = subData[nodeCode];
        if (subData && subData.currentId) {
          ids.push(subData.currentId);
        }
        //å»é¤nodeCodeçs
        nodeCode ? nodeCodeArray.push(nodeCode.substring(0, nodeCode.length - 1)) : nodeCodeArray.push(nodeCode);
      })
    }
    return { nodeCodes: nodeCodeArray, ids: ids };
  }
  /**
   * è·åå®æ´è·¯å¾
   * fixed by justin: æ ¹æ®bindingPathï¼å¦ææ¯ä»ä»è¡¨ï¼éè¦è·åä¸»è¡¨æ°æ®idåä»è¡¨æ°æ®id
   */
  private getPath(): string {
    const bindingPath = this.viewModel.bindingPath;
    const rid = this.viewModel.bindingData.list.currentId; // rootè¡¨æ°æ®id
    let path = '/' + rid;

    const subPaths = bindingPath.split('/').filter(p => p);
    if (subPaths.length > 0) {
      // eg:bindingPathå½¢å¦/edus/grades,splitåæ¯['edus', 'grades']
      let subData: any = this.viewModel.bindingData;
      for (let index = 1; index < subPaths.length - 1; index++) {
        const subPath = subPaths[index];
        subData = subData[subPath];
        if (!subData || !subData.currentId) {
          throw Error(`è·åå­è¡¨å®æ´è·¯å¾åºéï¼æ¾ä¸å°${subData}å¯¹åºçå­è¡¨ï¼æå¯¹åºå­è¡¨æ²¡æå½åè¡ã`);
        }
        path += `/${subPath}/${subData.currentId}`;
      }
    }
    path += '/' + subPaths[subPaths.length - 1];

    return path;
  }
}
export { SubTreeDataService };
